/** MAC / BLE name: strip colons, trim, lowercase (no length validation). */
function macWithoutColonsLower(s: string): string {
  return (s ?? "").replace(/:/g, "").trim().toLowerCase()
}

/** 12 lowercase hex chars without colons, or null if invalid. */
export function normalizeMac12(s: string): string | null {
  const normalized = macWithoutColonsLower(s)
  return /^[0-9a-f]{12}$/.test(normalized) ? normalized : null
}

/** BLE device name as 12 hex digits → `AA:BB:CC:DD:EE:FF`. Empty string when normalizeMac12 rejects input. */
export function formatMacWithColons(macString: string): string {
  const normalized = normalizeMac12(macString)
  if (normalized === null) {
    return ""
  }

  const parts: string[] = []
  for (let i = 0; i < 12; i += 2) {
    parts.push(normalized.slice(i, i + 2).toUpperCase())
  }

  return parts.join(":")
}

/**
 * MAC string for bluetooth console logs (Bugfender) as `AA:BB:CC:DD:EE:FF`.
 * Accepts advertised device name or socket `mac_address`; undefined when empty.
 */
export function macAddressForBleLog(
  macOrDeviceName: string | null | undefined,
): string | undefined {
  const trimmed = (macOrDeviceName ?? "").trim()
  if (!trimmed) {
    return undefined
  }

  const formatted = formatMacWithColons(trimmed)
  if (!formatted) {
    return undefined
  }

  return formatted
}

/** Spreads into console detail objects only when a MAC is present. */
export function bleLogMacFields(
  macOrDeviceName: string | null | undefined,
): { mac_address: string } | Record<string, never> {
  const mac_address = macAddressForBleLog(macOrDeviceName)
  return mac_address ? { mac_address } : {}
}

/** Formats each MAC for bluetooth console logs (colon-separated when valid). */
export function formatMacAddressesForBleLog(
  macOrDeviceNames: readonly string[],
): string[] {
  return macOrDeviceNames
    .map((mac) => formatMacWithColons(mac))
    .filter((mac) => mac !== "")
}
